/**
 * Departments and cost centers are the two organization dimensions a user can rename,
 * inactivate and reactivate. Every such change writes one audit row (`sys_audit_log`), and these
 * types are what the history endpoints return.
 */
export type OrgDimensionKind = 'department' | 'cost_center';

export type DimensionHistoryAction =
  'create' | 'rename' | 'update' | 'inactivate' | 'reactivate' | 'delete' | 'link' | 'unlink';

export interface DimensionHistoryChange {
  before: string | null;
  after: string | null;
}

export interface DimensionHistoryEntry {
  logId: string;
  /** The department or cost center the row is about. */
  resourceId: string;
  /** Its name when the change was made (names can change later). */
  resourceName: string | null;
  action: DimensionHistoryAction;
  actorUserId: string | null;
  actorName: string | null;
  timestamp: string;
  /** Field → before/after. Empty for create, delete, link and unlink. */
  changes: Record<string, DimensionHistoryChange>;
  /** Free text the user typed when inactivating. */
  reason: string | null;
  /** Set when the change was a side effect of inactivating a parent. */
  cascadedFromId: string | null;
  cascadedFromName: string | null;
  /** The other side of a link or unlink: a cost center for a department, and the reverse. */
  counterpartId: string | null;
  counterpartName: string | null;
}

export interface InactivateDimensionRequest {
  reason?: string;
}

export interface InactivatedDimensionItem {
  id: string;
  name: string;
}

export interface InactivateDimensionResponse {
  /** The item itself first, then every active descendant that was inactivated with it. */
  inactivated: InactivatedDimensionItem[];
}

/** Stable error codes of the inactivate and reactivate endpoints. The web maps them to pt-BR copy. */
export const DIMENSION_ERROR_CODES = {
  /** Reactivating an item whose parent is still inactive. */
  PARENT_INACTIVE: 'DIMENSION_PARENT_INACTIVE',
  /** A new write (parent, link) points at an inactive item. */
  TARGET_INACTIVE: 'DIMENSION_TARGET_INACTIVE',
  ALREADY_INACTIVE: 'DIMENSION_ALREADY_INACTIVE',
  ALREADY_ACTIVE: 'DIMENSION_ALREADY_ACTIVE',
  /** A department name or cost center code that another item of the entity already uses. */
  NAME_EXISTS: 'DIMENSION_NAME_EXISTS',
} as const;

export type DimensionErrorCode = (typeof DIMENSION_ERROR_CODES)[keyof typeof DIMENSION_ERROR_CODES];
